// Azure Storage Service for file uploads, sized for very large files.
// Uses backend-minted SAS and manual staged block uploads. The signature is
// renewed during the transfer so an upload is not bounded by one token, and an
// interrupted transfer resumes from the blocks the service already holds
// rather than restarting.
// Publishes cross-tab progress via BroadcastChannel 'uploads'.

import {apiConfig} from '@/config/api';
import {getLocalDateString} from '@/utils/timezoneUtils';
import {secureRandomFloat} from '@/utils/secureRandom';
import {authHeaders} from '@/utils/authHeaders';
import {BlockBlobClient} from '@azure/storage-blob';

interface AzureStorageConfig {
  accountName: string;
  accountKey: string; // not used on frontend
  containerName: string;
  directoryName: string;
}

interface UploadOptions {
  onProgress?: (progress: {
    percentage: number;
    loaded: number;
    total: number;
  }) => void;
  signal?: AbortSignal;
  overwrite?: boolean;
  // Per-upload tunables
  chunkConcurrency?: number; // number of staged blocks in flight
  blockSizeMB?: number; // override auto selection
  stallTimeoutMs?: number; // deadline per request before it is retried
}

interface AzureStorageService {
  uploadFile: (
    file: File,
    fileName: string,
    moderatorId: string,
    homeId: string,
    options?: UploadOptions
  ) => Promise<string>;
  checkFileExists: (fileName: string) => Promise<boolean>;
  deleteFile: (filePath: string) => Promise<void>;
}

export interface AzureBlobItem {
  name: string;
  url: string;
  size: number;
  lastModified: string | null;
  contentType: string;
  etag?: string;
  type?: string;
}

// Azure Storage Configuration (env-driven; used to build blob paths only)
const AZURE_CONFIG: AzureStorageConfig = {
  accountName: import.meta.env.VITE_AZURE_ACCOUNT_NAME || '',
  accountKey: import.meta.env.VITE_AZURE_ACCOUNT_KEY || '', // not used on frontend
  containerName: import.meta.env.VITE_AZURE_CONTAINER || '',
  directoryName: import.meta.env.VITE_AZURE_DIRECTORY ,
};

// Cross-tab broadcast channel
const makeChannel = () => {
  try {
    return new BroadcastChannel('uploads');
  } catch {
    return null as any;
  }
};
const uploadChannel = makeChannel();

// Track active uploads by blobPath for cross-tab abort
const activeControllers = new Map<string, AbortController>();

if (uploadChannel) {
  uploadChannel.onmessage = (evt: MessageEvent) => {
    const msg = evt.data || {};
    if (msg && msg.type === 'abort' && typeof msg.blobPath === 'string') {
      const ctrl = activeControllers.get(msg.blobPath);
      if (ctrl) {
        try {
          ctrl.abort();
        } catch {}
      }
    }
  };
}

export interface UploadLifecycleEvent {
  blobPath: string;
  percentage: number;
  loaded: number;
  total: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
}

// Listeners inside this browsing context. The cross-tab channel deliberately
// does not deliver to its own context, and a transfer outlives the screen that
// started it, so anything wanting live progress subscribes here instead of
// relying on the callback passed at upload time.
const progressListeners = new Set<(event: UploadLifecycleEvent) => void>();

const emitUploadEvent = (event: UploadLifecycleEvent) => {
  progressListeners.forEach((listener) => {
    try {
      listener(event);
    } catch {}
  });
};

/**
 * Watches every upload running in this context, including ones started before
 * the caller existed. Returns a function that stops watching.
 */
export const subscribeUploadProgress = (
  listener: (event: UploadLifecycleEvent) => void
): (() => void) => {
  progressListeners.add(listener);
  return () => {
    progressListeners.delete(listener);
  };
};

/** Whether a transfer for this destination is running in this context. */
export const isUploadActive = (blobPath: string): boolean =>
  activeControllers.has(blobPath);

/**
 * Stops an upload in progress. The transfer may be owned by this tab or by
 * another one, so the local controller is aborted and the other tabs are told
 * as well. Returns whether this tab was the one running it.
 *
 * Blocks already staged are left with the service, so starting the same upload
 * again continues from where it stopped rather than from the beginning.
 */
export const abortUpload = (blobPath: string): boolean => {
  const controller = activeControllers.get(blobPath);
  if (controller) {
    try {
      controller.abort();
    } catch {}
    activeControllers.delete(blobPath);
  }
  try {
    uploadChannel?.postMessage({type: 'abort', blobPath});
  } catch {}
  return !!controller;
};

// Build container-relative path: <directory>/<originalFileName>
const buildBlobPath = (fileName: string) =>
  `${AZURE_CONFIG.directoryName}/${fileName}`;

/**
 * Get directory name with validation
 * @returns Directory name or throws error if not configured
 * @throws Error if directory name is not configured
 */
export const getDirectoryName = (): string => {
  const dir = AZURE_CONFIG.directoryName;
  if (!dir || dir.trim() === '') {
    throw new Error('Directory name is not configured. Please set VITE_AZURE_DIRECTORY environment variable.');
  }
  return dir;
};

/**
 * Get directory name with fallback (returns empty string if not configured)
 * @returns Directory name or empty string
 */
export const getDirectoryNameSafe = (): string => {
  return AZURE_CONFIG.directoryName || '';
};

// OD-1344: uploads live at <environment>/<project>/<date>/<file>. The project
// folder is assigned by the backend (a slug of the OneForma project name, fixed
// for the life of the project), so it is fetched once per project and cached.
// Path builders are synchronous, so every upload flow awaits
// loadProjectStorageFolder before building a path.
const projectFolderCache = new Map<string, string>();
const projectFolderRequests = new Map<string, Promise<string>>();

export const loadProjectStorageFolder = (projectId: string): Promise<string> => {
  if (!projectId) {
    return Promise.reject(new Error('A project is required to upload files.'));
  }
  const cached = projectFolderCache.get(projectId);
  if (cached) return Promise.resolve(cached);
  const pending = projectFolderRequests.get(projectId);
  if (pending) return pending;

  const request = (async () => {
    const base = apiConfig.baseURL.replace(/\/$/, '');
    const resp = await fetch(
      `${base}/uploads/project-folder/${encodeURIComponent(projectId)}`,
      {headers: authHeaders()}
    );
    const body = await resp.json().catch(() => null);
    const folder = body?.data?.folder;
    if (!resp.ok || typeof folder !== 'string' || !folder) {
      throw new Error(
        body?.message || `Failed to resolve storage folder for project (status ${resp.status})`
      );
    }
    projectFolderCache.set(projectId, folder);
    return folder;
  })().finally(() => projectFolderRequests.delete(projectId));

  projectFolderRequests.set(projectId, request);
  return request;
};

/** The project's storage folder; loadProjectStorageFolder must have resolved first. */
export const getProjectStorageFolder = (projectId: string): string => {
  const folder = projectFolderCache.get(projectId);
  if (!folder) {
    throw new Error(
      `Storage folder for project ${projectId || '(none)'} is not loaded; call loadProjectStorageFolder first.`
    );
  }
  return folder;
};

/** Test hook: forget every cached project folder. */
export const clearProjectStorageFolderCache = (): void => {
  projectFolderCache.clear();
  projectFolderRequests.clear();
};

/**
 * Folder name for the extension level of an upload path. Lower-cased, and
 * files without an extension are grouped under a single known name rather
 * than creating a folder per oddity.
 */
export const fileExtensionFolder = (fileName: string): string => {
  const parts = String(fileName ?? '').split('.');
  if (parts.length > 1) {
    const last = parts[parts.length - 1].toLowerCase();
    if (last) return last;
  }
  return 'other';
};

/**
 * Build the path for a new upload:
 * {directory}/{projectFolder}/{uploadDate}/{extension}/{fileName}
 * The file name is used exactly as given. `extension` overrides the folder
 * derived from the name, for callers that group a file somewhere else.
 */
export const buildProjectFilePath = (
  projectId: string,
  uploadDate: string,
  fileName: string,
  directoryName?: string,
  extension?: string
): string => {
  const dir = directoryName || getDirectoryNameSafe();
  if (!dir) {
    throw new Error('Directory name is required to build file path');
  }
  const extensionFolder = extension || fileExtensionFolder(fileName);
  return `${dir}/${getProjectStorageFolder(projectId)}/${uploadDate}/${extensionFolder}/${fileName}`;
};

/**
 * Build a path in the layout used before OD-1344:
 * {directory}/{uploadDate}/{extension}/{fileName}
 * Only for locating files stored under that layout — new uploads use
 * buildProjectFilePath.
 */
export const buildFilePathWithDirectory = (
  uploadDate: string,
  fileExtension: string,
  fileName: string,
  directoryName?: string
): string => {
  const dir = directoryName || getDirectoryNameSafe();
  if (!dir) {
    throw new Error('Directory name is required to build file path');
  }
  return `${dir}/${uploadDate}/${fileExtension}/${fileName}`;
};

/**
 * Build backup path with directory prefix
 * Format: {directory}/.backup/{projectFolder}/{date}/{extension}/{fileName}
 * (without the project level when no project folder is given)
 * @param date - Date in YYYY-MM-DD format
 * @param fileExtension - File extension
 * @param fileName - File name
 * @param directoryName - Optional directory name (defaults to configured directory)
 * @param projectFolder - Optional project storage folder (loadProjectStorageFolder)
 * @returns Backup path with directory prefix
 */
export const buildBackupPath = (
  date: string,
  fileExtension: string,
  fileName: string,
  directoryName?: string,
  projectFolder?: string
): string => {
  const dir = directoryName || getDirectoryNameSafe();
  // One .backup per environment, split by project beneath it, so a file only
  // shares a backup location with files of the same project.
  const projectSegment = projectFolder ? `${projectFolder}/` : '';
  if (!dir) {
    // Fallback to root .backup if directory not configured (shouldn't happen)
    return `.backup/${projectSegment}${date}/${fileExtension}/${fileName}`;
  }
  return `${dir}/.backup/${projectSegment}${date}/${fileExtension}/${fileName}`;
};

const DATE_SEGMENT = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Whether a stored path already starts at an environment folder. Relative
 * paths begin with a date or `.backup` (or are a bare file name); anything
 * else is rooted, including paths under an environment folder that has since
 * been renamed (e.g. hummus-prd -> onedata-prd, OD-1344), which must keep
 * resolving where they were written.
 */
export const isRootedBlobPath = (filePath: string): boolean => {
  const slash = filePath.indexOf('/');
  if (slash <= 0) return false;
  const first = filePath.slice(0, slash);
  return first !== '.backup' && !DATE_SEGMENT.test(first);
};

/**
 * Ensure file path includes directory prefix
 * If path is relative (see isRootedBlobPath), adds the configured directory
 * @param filePath - File path that may or may not include directory prefix
 * @param directoryName - Optional directory name (defaults to configured directory)
 * @returns File path with directory prefix
 */
export const ensureDirectoryPrefix = (
  filePath: string,
  directoryName?: string
): string => {
  if (!filePath) return filePath;

  const dir = directoryName || getDirectoryNameSafe();
  if (!dir) return filePath; // No directory configured, return as-is

  if (filePath.startsWith(`${dir}/`) || isRootedBlobPath(filePath)) {
    return filePath;
  }

  // Add directory prefix
  return `${dir}/${filePath}`;
};

// Tunables via env (optional)
const ENV_BLOCK_SIZE_MB_RAW = (import.meta as any).env
  ?.VITE_AZURE_BLOCK_SIZE_MB as string | undefined;
const ENV_BLOCK_SIZE_MB = Number(ENV_BLOCK_SIZE_MB_RAW);
const HAS_ENV_BLOCK_SIZE =
  !!ENV_BLOCK_SIZE_MB_RAW && !Number.isNaN(ENV_BLOCK_SIZE_MB);
const MB = 1024 * 1024;
const GiB = 1024 * 1024 * 1024;
const DEFAULT_BLOCK_SIZE_MB = 8; // safe default
const INTERNAL_CHUNK_CONCURRENCY =
  Number(import.meta.env.VITE_AZURE_BLOCK_CONCURRENCY || '') || 4; // default 4

// A block blob accepts at most 50,000 blocks. Target a lower number so the
// block size grows with the file instead of hitting the service ceiling, and
// so a few re-staged blocks never push the count over the edge.
const MAX_BLOCKS_TARGET = 45000;
// Upper bound on block size. Large enough that the count ceiling is reachable
// only for multi-terabyte files, small enough to stay a sane HTTP request.
const MAX_BLOCK_SIZE_BYTES = 256 * MB;
// Rough cap on bytes held in memory at once (block size x blocks in flight).
// Concurrency is reduced rather than block size so throughput stays high on
// large files without growing the browser's heap.
const MAX_IN_FLIGHT_BYTES = 256 * MB;

// A request that stops making progress must fail rather than hang. When a
// machine sleeps or a network disappears without closing the connection, the
// socket stays half open: writes go nowhere, no error is ever raised and the
// promise never settles, so nothing retries and nothing resumes. Every request
// therefore carries its own deadline, generous enough not to punish slow links.
// A deadline fixed in advance has to assume a transfer rate, and any
// assumption is wrong for someone: too tight and a slow link cancels blocks
// that were transferring perfectly well, too loose and a dead connection sits
// there for a long time. The deadline is therefore derived from the pace this
// upload is actually achieving, so a transfer is only cut off when it stops
// making the progress it had been making.
const STALL_DEADLINE_FLOOR_MS = 2 * 60 * 1000;
// Before any block has completed there is nothing to measure, so the first
// ones get a deliberately forgiving window.
const STALL_DEADLINE_INITIAL_MS = 10 * 60 * 1000;
// How far past the going rate a single block may run before it is treated as
// stalled rather than slow.
const STALL_PACE_FACTOR = 5;
const COMMIT_TIMEOUT_MS = 5 * 60 * 1000;

export interface BlockPace {
  /** Records how long a block took, in milliseconds. */
  record: (durationMs: number) => void;
  /** Deadline for the next block, based on the pace observed so far. */
  deadlineMs: () => number;
}

/**
 * Tracks how long blocks are taking so the stall deadline can follow the link
 * rather than a guess about it. A fast connection gets a tight deadline and a
 * slow one gets a generous deadline, from the same rule.
 */
export const createBlockPace = (override?: number): BlockPace => {
  let completed = 0;
  let totalMs = 0;
  return {
    record: (durationMs: number) => {
      if (!Number.isFinite(durationMs) || durationMs <= 0) return;
      completed++;
      totalMs += durationMs;
    },
    deadlineMs: () => {
      if (typeof override === 'number') return override;
      if (completed === 0) return STALL_DEADLINE_INITIAL_MS;
      const average = totalMs / completed;
      return Math.max(STALL_DEADLINE_FLOOR_MS, average * STALL_PACE_FACTOR);
    },
  };
};

/**
 * Runs one request under its own deadline, alongside the caller's abort
 * signal. A deadline breach is reported as a timeout so it is retried, while a
 * caller-initiated abort keeps its own identity and stops the transfer.
 */
export const withStallTimeout = async <T>(
  work: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  callerSignal: AbortSignal
): Promise<T> => {
  if (callerSignal.aborted) throw new Error('Upload aborted');

  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const relayAbort = () => controller.abort();
  callerSignal.addEventListener('abort', relayAbort, {once: true});

  try {
    return await work(controller.signal);
  } catch (error) {
    if (timedOut && !callerSignal.aborted) {
      throw new Error(
        `Request timeout after ${Math.round(timeoutMs / 1000)}s without a response`
      );
    }
    throw error;
  } finally {
    clearTimeout(timer);
    callerSignal.removeEventListener('abort', relayAbort);
  }
};

// Lifetime asked for when signing a staged upload. It does not bound the
// transfer: a signature nearing expiry is replaced and staging continues, so
// an upload runs for as long as the file needs regardless of this value.
const SAS_REQUEST_MINUTES = 60;
// A signature is replaced once this share of its life remains, clamped to the
// window below. Derived from the lifetime actually granted rather than from
// what was asked for, so it adapts if the signing service grants something
// else entirely.
const SAS_RENEW_AT_REMAINING = 0.2;
const MIN_RENEW_LEAD_MS = 60 * 1000;
const MAX_RENEW_LEAD_MS = 10 * 60 * 1000;

const determineBlockSizeBytes = (
  file: File,
  overrideBlockSizeMB?: number
): number => {
  if (
    typeof overrideBlockSizeMB === 'number' &&
    !Number.isNaN(overrideBlockSizeMB)
  ) {
    const clamped = Math.max(4, Math.min(overrideBlockSizeMB, 64));
    return enforceBlockCountCeiling(clamped * MB, file.size);
  }
  if (HAS_ENV_BLOCK_SIZE) {
    const clamped = Math.max(4, Math.min(ENV_BLOCK_SIZE_MB, 64));
    return enforceBlockCountCeiling(clamped * MB, file.size);
  }

  let sizeMb = DEFAULT_BLOCK_SIZE_MB;
  const nav: any = typeof navigator !== 'undefined' ? navigator : {};
  const connection: any =
    nav.connection || nav.webkitConnection || nav.mozConnection;
  const effectiveType: string | undefined = connection?.effectiveType;
  const downlink: number =
    typeof connection?.downlink === 'number' ? connection.downlink : 0; // Mbps
  const deviceMemory: number =
    typeof nav.deviceMemory === 'number' ? nav.deviceMemory : 4; // GB

  if (effectiveType && /(^|-)2g|(^|-)3g|slow-2g/.test(effectiveType)) {
    sizeMb = 4; // smaller blocks on slow networks
  } else if (downlink >= 20) {
    sizeMb = 16; // faster link can handle larger blocks
  }

  // Device memory shapes the preference, but it is applied before the
  // file-size floor below so it can never drag a very large file back down to
  // a block size that would need too many blocks.
  if (deviceMemory < 4) {
    sizeMb = Math.min(sizeMb, 8);
  } else if (deviceMemory >= 8 && downlink >= 30) {
    sizeMb = Math.max(sizeMb, 24);
  }

  if (file.size > 32 * GiB) {
    sizeMb = Math.max(sizeMb, 32);
  } else if (file.size > 8 * GiB) {
    sizeMb = Math.max(sizeMb, 16);
  }

  const preferred = Math.max(4, Math.min(sizeMb, 64)) * MB;
  return enforceBlockCountCeiling(preferred, file.size);
};

/**
 * Raise a block size until the file fits inside the block-count budget.
 * Keeps the upload valid for any file size rather than a fixed ceiling.
 */
const enforceBlockCountCeiling = (
  blockSizeBytes: number,
  fileSize: number
): number => {
  const required = Math.ceil(fileSize / MAX_BLOCKS_TARGET);
  const chosen = Math.max(blockSizeBytes, required);
  return Math.min(Math.max(chosen, 4 * MB), MAX_BLOCK_SIZE_BYTES);
};

/**
 * Blocks in flight, reduced so the buffered bytes stay bounded when the block
 * size grows for a very large file.
 */
const resolveConcurrency = (
  requested: number,
  blockSizeBytes: number
): number => {
  const byMemory = Math.floor(MAX_IN_FLIGHT_BYTES / blockSizeBytes);
  return Math.max(1, Math.min(requested, Math.max(1, byMemory)));
};

// Basic helpers for resilient networking
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// How long to keep waiting for an interface to come back before giving up and
// letting the normal retry ladder run. A machine that sleeps for an hour and
// wakes with the transfer intact is the case this exists for.
const MAX_OFFLINE_WAIT_MS = 30 * 60 * 1000;

/**
 * Waits until the browser reports a network again.
 *
 * A suspended interface (a machine going to sleep, Wi-Fi switched off) fails
 * requests instantly, so without this the retry budget is spent in seconds on
 * a network that is not there, and a transfer that could have continued on
 * wake is lost instead.
 */
export const waitForOnline = async (
  callerSignal?: AbortSignal,
  maxWaitMs: number = MAX_OFFLINE_WAIT_MS
): Promise<void> => {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) return;

  await new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      globalThis.removeEventListener?.('online', finish);
      callerSignal?.removeEventListener('abort', finish);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(finish, maxWaitMs);
    globalThis.addEventListener?.('online', finish, {once: true});
    callerSignal?.addEventListener('abort', finish, {once: true});
  });
};

const errorStatusOf = (error: any): number | undefined =>
  error?.statusCode || error?.status || error?.response?.status;

const errorCodeOf = (error: any): string =>
  String(
    error?.code ||
      error?.details?.errorCode ||
      error?.response?.headers?.get?.('x-ms-error-code') ||
      ''
  );

/**
 * True when the signed URL has expired or is no longer accepted. These are
 * recoverable by minting a fresh signature, unlike a genuine permission
 * problem, so they are retried rather than failing the transfer.
 */
const isExpiredSignatureError = (error: any): boolean => {
  const status = errorStatusOf(error);
  if (status !== 403) return false;
  const haystack = `${errorCodeOf(error)} ${String(error?.message || '')}`;
  return /AuthenticationFailed|AuthorizationFailure|Signature|expired|Server failed to authenticate/i.test(
    haystack
  );
};

export const isRetryableUploadError = (error: any): boolean => {
  const status = errorStatusOf(error);
  if (typeof status === 'number') {
    if (status === 429) return true;
    if (status >= 500 && status < 600) return true;
  }
  if (isExpiredSignatureError(error)) return true;
  const message = String(error?.message || '');
  return /ETIMEDOUT|ECONNRESET|ENETRESET|EAI_AGAIN|NetworkError|Failed to fetch|timeout|temporar/i.test(
    message
  );
};

async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  options: {
    retries?: number;
    baseMs?: number;
    maxMs?: number;
    jitter?: boolean;
    /** Runs before each retry, e.g. to replace an expired signature. */
    onRetry?: (error: any) => Promise<void> | void;
  } = {}
): Promise<T> {
  const retries = options.retries ?? 5;
  const baseMs = options.baseMs ?? 500;
  const maxMs = options.maxMs ?? 8000;
  const jitter = options.jitter ?? true;
  let attempt = 0;
  let lastError: any;

  while (attempt <= retries) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      if (!isRetryableUploadError(error) || attempt === retries) {
        throw error;
      }
      await waitForOnline();
      if (options.onRetry) {
        await options.onRetry(error);
      }
      const backoff = Math.min(baseMs * Math.pow(2, attempt), maxMs);
      const waitMs = jitter
        ? Math.round(backoff * (0.5 + secureRandomFloat()))
        : backoff;
      await sleep(waitMs);
      attempt++;
    }
  }
  throw lastError;
}

// Request a SAS URL from backend for a specific blob path with desired permissions
export const requestSasUrl = async (
  blobPath: string,
  permissions: string,
  expiryMinutes = 240
): Promise<string> => {
  // Use path as-is - don't normalize here
  // DataUploads uploads include directory prefix, submit data uploads don't
  // Normalization should only happen in delete/copy operations where we know the stored format
  const normalizedPath = blobPath;
  const fnUrlRaw = (import.meta as any).env?.VITE_AZURE_FUNCTION_SAS_URL as
    | string
    | undefined;
  const fnKey = (import.meta as any).env?.VITE_AZURE_FUNCTION_KEY as
    | string
    | undefined;

  const buildFunctionUrl = (): string | null => {
    if (!fnUrlRaw) return null;
    try {
      const u = new URL(fnUrlRaw);
      // Always add code as query parameter if available (Function App authentication)
      if (fnKey && !u.searchParams.has('code')) {
        u.searchParams.set('code', fnKey);
      }
      return u.toString();
    } catch {
      return fnUrlRaw; // best effort
    }
  };

  const functionUrl = buildFunctionUrl();

  return retryWithBackoff(
    async () => {
      const backendUrl = `${apiConfig.baseURL.replace(/\/$/, '')}/uploads/sas`;
      
      // Try Function App first if configured
      if (functionUrl) {
        try {
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
          };
          
          // Don't send x-functions-key header if code is already in URL
          // The Function App uses the code query parameter for authentication
          
          // Function App request body - match backend format exactly
          const requestBody: any = {
            path: normalizedPath,
            permissions,
            expiryMinutes,
          };
          
          // Don't send container - Function App should use its own config
          // The blobPath already contains the full path structure
          const resp = await fetch(functionUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody),
          });
          
          if (resp.ok) {
            const data = await resp.json();
            return data.url as string;
          }
          
          // If Function App fails, try to get error details
          const text = await resp.text();
          let errorDetails = text;
          try {
            const errorJson = JSON.parse(text);
            errorDetails = errorJson.details || errorJson.error || text;
          } catch {}
          
          // If Function App fails with 500 (config/auth error), fallback to backend
          // For other errors (400, 404, etc), also fallback as they might be Function App issues
          if (resp.status >= 500 || resp.status === 400) {
            // Fall through to backend fallback
          } else if (resp.status === 404 || resp.status === 403 || resp.status === 410) {
            // File not found errors - fallback to backend to check if file exists there
            // Fall through to backend fallback
          } else {
            // For other errors, throw immediately
            const error: any = new Error(
              `Failed to obtain SAS URL from Function App: ${resp.status} ${errorDetails}`
            );
            error.status = resp.status;
            error.details = errorDetails;
            throw error;
          }
        } catch {
          // Network/CORS errors - fallback to backend
        }
      }
      
      // Fallback to backend (or primary if Function App not configured).
      // This route requires a session, so the token always goes with it.
      const headers = authHeaders({'Content-Type': 'application/json'});
      
      const requestBody = {path: normalizedPath, permissions, expiryMinutes};
      
      const resp = await fetch(backendUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });
      
      if (!resp.ok) {
        const text = await resp.text();
        console.error(`Failed to get SAS token from backend:`, resp.status, text);
        const error: any = new Error(
          `Failed to obtain SAS URL: ${resp.status} ${text}`
        );
        error.status = resp.status;
        if (resp.status === 404 || resp.status === 403 || resp.status === 410) {
          error.isFileNotFound = true;
        }
        throw error;
      }
      const data = await resp.json();
      return data.url as string;
    },
    {retries: 5, baseMs: 500, maxMs: 8000, jitter: true}
  );
};

/**
 * Expiry timestamp of a signed URL, read from its `se` parameter. Returns 0
 * when it cannot be read, so callers fall back to the lifetime they asked for.
 */
export const parseSasExpiry = (sasUrl: string): number => {
  try {
    const expiry = new URL(sasUrl).searchParams.get('se');
    if (!expiry) return 0;
    const parsed = Date.parse(expiry);
    return Number.isNaN(parsed) ? 0 : parsed;
  } catch {
    return 0;
  }
};

export interface RenewingBlobClient {
  /** Current client, replaced automatically as the signature nears expiry. */
  get: () => Promise<BlockBlobClient>;
  /** Forces a replacement on the next get(), e.g. after a rejected signature. */
  invalidate: () => void;
  /** Most recently minted signed URL. */
  url: () => string;
  /** How many times the signature has been replaced, for diagnostics. */
  renewals: () => number;
}

/**
 * Wraps a blob client so a transfer that outlives a single signature keeps
 * working. The signature is replaced shortly before it expires and on demand,
 * which is what allows an upload to run for many hours on a slow link.
 */
export const createRenewingBlobClient = (
  blobPath: string,
  permissions: string,
  expiryMinutes: number = SAS_REQUEST_MINUTES
): RenewingBlobClient => {
  let client: BlockBlobClient | null = null;
  let currentUrl = '';
  let expiresAt = 0;
  let renewAt = 0;
  let renewals = -1; // first mint is not a renewal
  let pending: Promise<BlockBlobClient> | null = null;

  const mint = async (): Promise<BlockBlobClient> => {
    const mintedAt = Date.now();
    const url = await requestSasUrl(blobPath, permissions, expiryMinutes);
    const parsed = parseSasExpiry(url);
    expiresAt = parsed || mintedAt + expiryMinutes * 60 * 1000;
    const lifetimeMs = Math.max(0, expiresAt - mintedAt);
    const lead = Math.min(
      MAX_RENEW_LEAD_MS,
      Math.max(MIN_RENEW_LEAD_MS, lifetimeMs * SAS_RENEW_AT_REMAINING)
    );
    // A lifetime shorter than the lead still has to be usable, so fall back to
    // renewing at the midpoint rather than immediately.
    renewAt =
      lead >= lifetimeMs ? mintedAt + lifetimeMs / 2 : expiresAt - lead;
    currentUrl = url;
    client = new BlockBlobClient(url);
    renewals++;
    return client;
  };

  return {
    get: async () => {
      const stale = !client || Date.now() >= renewAt;
      if (!stale && client) return client;
      // Collapse concurrent renewals so blocks in flight share one signature.
      pending =
        pending ??
        mint().finally(() => {
          pending = null;
        });
      return pending;
    },
    invalidate: () => {
      renewAt = 0;
      expiresAt = 0;
    },
    url: () => currentUrl,
    renewals: () => Math.max(0, renewals),
  };
};

/**
 * Thrown by copyBlob when storage reports that the source blob does not exist,
 * as distinct from a failure to reach storage at all.
 */
export class SourceBlobNotFoundError extends Error {
  readonly code = 'SourceBlobNotFound';

  constructor(readonly blobPath: string) {
    super(`Source file not found: ${blobPath}`);
    this.name = 'SourceBlobNotFoundError';
  }
}

/**
 * Copy a blob from source path to destination path using SAS tokens
 * @param sourcePath - Source blob path
 * @param destPath - Destination blob path
 * @returns Promise that resolves when copy completes
 */
export const copyBlob = async (
  sourcePath: string,
  destPath: string
): Promise<void> => {
  // Use paths as-is - don't normalize
  // DataUploads files are stored WITH directory prefix (e.g., onedata-dev/2026-02-09/mp4/file.mp4)
  // Submit data files are stored WITHOUT directory prefix (e.g., 2026-02-09/mp4/file.mp4)
  // Both formats are valid and should be used as stored in the database
  const normalizedSourcePath = sourcePath; // Use source path as stored in database
  const normalizedDestPath = destPath; // Use dest path as-is (already in correct format)
  
  // Get read SAS for source and read+create+write SAS for destination
  // create+write needed for copy operation
  const sourceSasUrl = await requestSasUrl(normalizedSourcePath, 'r', 15);
  const destSasUrl = await requestSasUrl(normalizedDestPath, 'rcw', 15);

  const sourceClient = new BlockBlobClient(sourceSasUrl);
  const destClient = new BlockBlobClient(destSasUrl);

  // Check if source exists
  const sourceExists = await sourceClient.exists();
  if (!sourceExists) {
    throw new SourceBlobNotFoundError(sourcePath);
  }

  // Start copy operation using beginCopyFromURL (returns a poller)
  // NOTE: copy is async on Azure side; polling will issue periodic status requests.
  // Never replace an existing blob: a copy onto a taken path fails instead, so
  // callers can pick another name rather than silently losing that blob.
  await destClient.beginCopyFromURL(sourceSasUrl, {
    conditions: {ifNoneMatch: '*'},
  });

  // Wait for copy to complete.
  // We implement our own polling so we can control frequency (avoids spamming the network).
  const pollIntervalMs = 2500;
  const maxAttempts = 120; // ~5 minutes
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const props = await destClient.getProperties();
    const status = props.copyStatus?.toLowerCase();
    if (!status || status === 'success') return;
    if (status === 'failed' || status === 'aborted') {
      throw new Error(
        `Copy failed: ${props.copyStatusDescription || 'Unknown error'}`
      );
    }
    await new Promise((r) => setTimeout(r, pollIntervalMs));
  }
  throw new Error('Copy operation did not complete in time');
};

/**
 * Delete a blob using SAS token
 * @param blobPath - Blob path to delete
 * @returns Promise that resolves when delete completes
 */
export const deleteBlob = async (blobPath: string): Promise<void> => {
  // Use path as-is - don't normalize
  // DataUploads files are stored WITH directory prefix, submit data files WITHOUT
  // Both formats are valid and should be used as stored in the database
  const normalizedPath = blobPath;
  
  // Get SAS token with delete permission. Avoid pre-flight exists() checks (HEAD) to prevent permission mismatches/noise.
  const sasUrl = await requestSasUrl(normalizedPath, 'd', 15);
  const blobClient = new BlockBlobClient(sasUrl);

  // Delete the blob (uses DELETE request, requires delete permission)
  // If it doesn't exist, treat as success.
  await blobClient.deleteIfExists();
};

/**
 * Check if a blob exists using SAS token
 * @param blobPath - Blob path to check
 * @returns Promise that resolves to true if blob exists, false otherwise
 */
export const blobExists = async (blobPath: string): Promise<boolean> => {
  try {
    // Use path as-is - don't normalize
    // DataUploads files are stored WITH directory prefix, submit data files WITHOUT
    // Both formats are valid and should be used as stored in the database
    const normalizedPath = blobPath;
    const sasUrl = await requestSasUrl(normalizedPath, 'r', 15);
    const blobClient = new BlockBlobClient(sasUrl);
    return await blobClient.exists();
  } catch {
    return false;
  }
};

// A deterministic id per block index is what makes an interrupted transfer
// resumable: the service can be asked which blocks it already holds, and the
// answer maps straight back to positions in the file.
const BLOCK_ID_PREFIX = 'b-';
const BLOCK_INDEX_DIGITS = 8;
const BLOCK_TAG_DIGITS = 8;
// Hard service limit on blocks per blob.
const MAX_BLOCKS_PER_BLOB = 50000;

/** 32-bit FNV-1a, rendered as fixed-width hex. */
const shortHash = (source: string): string => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < source.length; i++) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(BLOCK_TAG_DIGITS, '0');
};

export interface BlockIdCodec {
  make: (index: number) => string;
  decode: (blockId: string) => number | null;
}

/**
 * Block ids for one specific file.
 *
 * The id carries both the position in the file and a tag derived from the
 * file's size and modification time. Position alone would let a half-finished
 * upload of one file be mistaken for progress on a different file of the same
 * size at the same path, and the committed blob would silently mix the two.
 * With the tag, blocks belonging to anything else simply do not decode, and
 * the upload starts over.
 *
 * Every id is the same length, which the service requires.
 */
export const createBlockIdCodec = (file: {
  size: number;
  lastModified?: number;
}): BlockIdCodec => {
  const tag = shortHash(`${file.size}:${file.lastModified ?? 0}`);
  const prefix = `${BLOCK_ID_PREFIX}${tag}-`;
  return {
    make: (index: number) =>
      btoa(`${prefix}${index.toString().padStart(BLOCK_INDEX_DIGITS, '0')}`),
    decode: (blockId: string): number | null => {
      try {
        const decoded = atob(blockId);
        if (!decoded.startsWith(prefix)) return null;
        const digits = decoded.slice(prefix.length);
        if (!/^\d+$/.test(digits)) return null;
        const index = parseInt(digits, 10);
        return Number.isFinite(index) && index >= 0 ? index : null;
      } catch {
        return null;
      }
    },
  };
};

export interface StagedBlocks {
  blockSize: number;
  indices: Set<number>;
}

/**
 * Ask the service which blocks of this blob it is already holding, so an
 * interrupted transfer continues instead of starting over.
 *
 * The service is the only trustworthy record here. Browser-side bookkeeping
 * can outlive the blocks themselves (uncommitted blocks are discarded after a
 * week) and committing a block the service no longer has fails the whole
 * upload, so the list is read back rather than remembered.
 *
 * Returns null whenever the staged blocks cannot be matched to this file with
 * certainty, which makes the caller upload everything, exactly as before.
 */
export const discoverStagedBlocks = async (
  client: BlockBlobClient,
  fileSize: number,
  codec: BlockIdCodec
): Promise<StagedBlocks | null> => {
  let blocks: Array<{index: number; size: number}>;
  try {
    const list = await client.getBlockList('uncommitted');
    blocks = (list.uncommittedBlocks || [])
      .map((block) => ({index: codec.decode(block.name), size: block.size}))
      .filter((block): block is {index: number; size: number} =>
        block.index !== null
      );
  } catch {
    // No such blob yet, or the listing is unavailable. Upload from the start.
    return null;
  }

  // Two blocks of the same size are needed to infer the size the interrupted
  // attempt used. A lone block could be a partial final block, which would
  // imply the wrong size and misplace every other block.
  if (blocks.length < 2) return null;
  const blockSize = Math.max(...blocks.map((block) => block.size));
  if (blockSize <= 0) return null;
  if (blocks.filter((block) => block.size === blockSize).length < 2) {
    return null;
  }

  const totalBlocks = Math.ceil(fileSize / blockSize);
  if (totalBlocks > MAX_BLOCKS_PER_BLOB) return null;

  const indices = new Set<number>();
  for (const block of blocks) {
    if (block.index >= totalBlocks) return null;
    const expected = Math.min(blockSize, fileSize - block.index * blockSize);
    // A short block means the source changed or the stage was truncated.
    if (block.size !== expected) return null;
    indices.add(block.index);
  }
  return {blockSize, indices};
};

/**
 * Diagnostics for long transfers. Block size, resumed count and throughput are
 * what distinguish a stalled upload from a slow link, and neither is
 * recoverable after the fact without a log line.
 */
const logUpload = (message: string) => {
  // eslint-disable-next-line no-console
  console.info(`[upload] ${message}`);
};

/** Discards bookkeeping written by the earlier browser-side resume attempt. */
const clearLegacyResumeState = (blobPath: string) => {
  try {
    localStorage.removeItem(
      `azure_upload_resume:${AZURE_CONFIG.containerName}/${blobPath}`
    );
  } catch {}
};

/**
 * Stage all pending blocks with bounded concurrency, resolving when every
 * block has been uploaded (or rejecting on first error / abort).
 * Extracted to module scope to keep upload callbacks within nesting limits.
 */
const stageAllBlocks = (params: {
  clientRef: RenewingBlobClient;
  file: File;
  blockSize: number;
  toUploadIndices: number[];
  concurrency: number;
  abortSignal: AbortSignal;
  totalBlocks: number;
  state: {uploadedBlocks: number[]};
  codec: BlockIdCodec;
  stallTimeoutMs?: number;
  updateProgress: () => void;
  externalSignal?: AbortSignal;
}): Promise<void> => {
  const {
    clientRef,
    file,
    blockSize,
    toUploadIndices,
    concurrency,
    abortSignal,
    totalBlocks,
    state,
    codec,
    stallTimeoutMs,
    updateProgress,
    externalSignal,
  } = params;

  const pace = createBlockPace(stallTimeoutMs);

  const stageOneBlock = (index: number, signalError: (e: any) => void) =>
    retryWithBackoff(
      async () => {
        const start = index * blockSize;
        const end = Math.min(start + blockSize, file.size);
        const slice = file.slice(start, end);
        // Resolved per attempt so a long transfer picks up a fresh signature.
        const client = await clientRef.get();
        const startedAt = Date.now();
        const result = await withStallTimeout(
          (signal) =>
            client.stageBlock(codec.make(index), slice, slice.size, {
              abortSignal: signal,
            }),
          pace.deadlineMs(),
          abortSignal
        );
        // Feeds the deadline for the blocks that follow.
        pace.record(Date.now() - startedAt);
        return result;
      },
      {
        retries: 5,
        baseMs: 500,
        maxMs: 7000,
        jitter: true,
        onRetry: (error) => {
          if (isExpiredSignatureError(error)) clientRef.invalidate();
        },
      }
    )
      .then(() => {
        if (!state.uploadedBlocks.includes(index)) {
          state.uploadedBlocks.push(index);
        }
        updateProgress();
      })
      .catch((e) => {
        console.error(`Block ${index + 1}/${totalBlocks} failed:`, e.message);
        signalError(e);
      });

  // Nothing left to stage, e.g. a resumed transfer that only needs committing.
  // The pump below never settles when there is no work to start.
  if (toUploadIndices.length === 0) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    let active = 0;
    let cursor = 0;
    let error: any = null;

    const pump = () => {
      if (error) return;
      while (active < concurrency && cursor < toUploadIndices.length) {
        const index = toUploadIndices[cursor++];
        active++;
        stageOneBlock(index, (e) => {
          error = e;
        }).finally(() => {
          active--;
          if (error) {
            reject(error);
            return;
          }
          if (cursor >= toUploadIndices.length && active === 0) {
            resolve();
          } else {
            pump();
          }
        });
      }
    };

    if (externalSignal) {
      externalSignal.addEventListener(
        'abort',
        () => reject(new Error('Upload aborted')),
        {once: true}
      );
    }

    pump();
  });
};

export const createAzureStorageService = async (
  moderatorId: string,
  homeId: string
): Promise<AzureStorageService> => {
  const service: AzureStorageService = {
    // Direct, resumable chunked upload using stageBlock/commitBlockList
    uploadFile: async (
      file: File,
      fileName: string,
      _moderatorId: string,
      _homeId: string,
      options?: UploadOptions
    ) => {
      // Wrapper function for actual upload logic (to enable retry)
      const performUpload = async (attemptNumber: number): Promise<string> => {
        // Validate Azure config before proceeding
        if (!AZURE_CONFIG.directoryName) {
          const errorMsg = 'Azure storage directory is not configured. Please set VITE_AZURE_DIRECTORY environment variable.';
          console.error(errorMsg);
          throw new Error(errorMsg);
        }
        
        // If fileName already contains path separators, use it as-is
        // Otherwise, prepend the directory name
        const normalizedFileName = fileName;
        const blobPath = normalizedFileName.includes('/') 
          ? normalizedFileName 
          : buildBlobPath(normalizedFileName);
        
        try {
          // Read+create+write, renewed as needed so a transfer that runs for
          // hours is not bounded by the lifetime of one signature.
          const clientRef = createRenewingBlobClient(blobPath, 'rcw');
          const client = await clientRef.get();

          // Announce start
          uploadChannel?.postMessage({
            type: 'started',
            blobPath,
            targetName: normalizedFileName,
          });

          clearLegacyResumeState(blobPath);

          // Continue an interrupted transfer when the service still holds
          // blocks for this blob, otherwise size blocks for this file. A file
          // that fits in one block has nothing to resume, so it skips the
          // listing rather than paying a round trip for it.
          const defaultBlockSize = determineBlockSizeBytes(
            file,
            options?.blockSizeMB
          );
          const codec = createBlockIdCodec(file);
          const staged =
            file.size > defaultBlockSize
              ? await discoverStagedBlocks(client, file.size, codec)
              : null;
          const blockSize = staged?.blockSize ?? defaultBlockSize;
          const totalBlocks = Math.ceil(file.size / blockSize);
          const alreadyStaged = staged?.indices ?? new Set<number>();

          const state = {
            version: 1,
            blockSize,
            uploadedBlocks: [...alreadyStaged],
            fileSize: file.size,
            lastModified: file.lastModified,
          };

          const toUploadIndices: number[] = [];
          for (let i = 0; i < totalBlocks; i++) {
            if (!alreadyStaged.has(i)) toUploadIndices.push(i);
          }

          // Stage blocks sequentially or with limited parallelism
          const requestedConcurrency =
            typeof options?.chunkConcurrency === 'number' &&
            !Number.isNaN(options.chunkConcurrency)
              ? options.chunkConcurrency
              : INTERNAL_CHUNK_CONCURRENCY;
          const concurrency = resolveConcurrency(
            Math.max(1, Math.min(requestedConcurrency, 16)),
            blockSize
          );

          const startedAt = Date.now();
          logUpload(
            `${blobPath} start size=${file.size} block=${blockSize} ` +
              `blocks=${totalBlocks} resumed=${alreadyStaged.size} ` +
              `concurrency=${concurrency} attempt=${attemptNumber}`
          );

          // Create an internal abort controller if caller didn't provide one
          const internalController = new AbortController();
          const abortSignal = options?.signal || internalController.signal;
          activeControllers.set(blobPath, internalController);

          const total = file.size;
          const updateProgress = () => {
            const loaded = Math.min(
              state.uploadedBlocks.length * blockSize,
              total
            );
            const percentage = Math.min(100, Math.round((loaded / total) * 100));
            options?.onProgress?.({percentage, loaded, total});
            emitUploadEvent({
              blobPath,
              percentage,
              loaded,
              total,
              status: 'uploading',
            });
            uploadChannel?.postMessage({
              type: 'progress',
              blobPath,
              targetName: normalizedFileName,
              loaded,
              total,
              percentage,
            });
          };

          updateProgress();

          await stageAllBlocks({
            clientRef,
            file,
            blockSize,
            toUploadIndices,
            concurrency,
            abortSignal,
            totalBlocks,
            state,
            codec,
            stallTimeoutMs: options?.stallTimeoutMs,
            updateProgress,
            externalSignal: options?.signal,
          });

          // Build ordered block list as an array of block IDs (strings)
          const blockList: string[] = Array.from({length: totalBlocks}, (_, i) =>
            codec.make(i)
          );
          await retryWithBackoff(
            async () => {
              const current = await clientRef.get();
              return withStallTimeout(
                (signal) =>
                  current.commitBlockList(blockList, {
                    blobHTTPHeaders: {
                      blobContentType: file.type || 'application/octet-stream',
                    },
                    abortSignal: signal,
                  }),
                options?.stallTimeoutMs ?? COMMIT_TIMEOUT_MS,
                abortSignal
              );
            },
            {
              retries: 5,
              baseMs: 500,
              maxMs: 8000,
              jitter: true,
              onRetry: (error) => {
                if (isExpiredSignatureError(error)) clientRef.invalidate();
              },
            }
          );

          activeControllers.delete(blobPath);

          const elapsedSec = Math.max(1, (Date.now() - startedAt) / 1000);
          logUpload(
            `${blobPath} done bytes=${file.size} ` +
              `elapsed=${Math.round(elapsedSec)}s ` +
              `throughput=${(file.size / elapsedSec / MB).toFixed(2)}MB/s ` +
              `renewals=${clientRef.renewals()}`
          );

          const cleanUrl = clientRef.url().split('?')[0];
          emitUploadEvent({
            blobPath,
            percentage: 100,
            loaded: file.size,
            total: file.size,
            status: 'completed',
          });
          uploadChannel?.postMessage({
            type: 'completed',
            blobPath,
            targetName: normalizedFileName,
          });
          options?.onProgress?.({
            percentage: 100,
            loaded: file.size,
            total: file.size,
          });
          return cleanUrl;
        } catch (error: any) {
          console.error('Upload failed:', error.message);
          activeControllers.delete(blobPath);
          const status =
            error?.statusCode || error?.status || error?.response?.status;
          const code =
            error?.code ||
            error?.details?.errorCode ||
            error?.response?.headers?.get?.('x-ms-error-code');
          const message = error?.message || 'Upload failed';
          
          // Don't send error message for retryable attempts (will be retried)
          if (attemptNumber >= 3) {
            emitUploadEvent({
              blobPath,
              percentage: 0,
              loaded: 0,
              total: file.size,
              status: 'error',
              error: message,
            });
            uploadChannel?.postMessage({
              type: 'error',
              blobPath,
              targetName: normalizedFileName,
              message,
              status,
              code,
            });
          }
          
          throw error; // Re-throw to trigger retry
        }
      }; // End of performUpload function
      
      // AUTO-RETRY LOGIC: Try upload up to 3 times with exponential backoff
      let lastError: any = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          return await performUpload(attempt);
        } catch (error: any) {
          lastError = error;
          
          // Don't retry if user manually aborted
          if (error?.message?.includes('aborted') || error?.message?.includes('abort')) {
            throw error;
          }
          
          // Don't retry on last attempt
          if (attempt >= 3) {
            console.error('All upload attempts failed');
            break;
          }
          
          // Wait before retry (exponential backoff: 2s, 4s), and never retry
          // into an interface that is still down.
          const delayMs = 2000 * attempt;
          await sleep(delayMs);
          await waitForOnline(options?.signal);
        }
      }
      
      // If we got here, all retries failed
      const status =
        lastError?.statusCode || lastError?.status || lastError?.response?.status;
      const code =
        lastError?.code ||
        lastError?.details?.errorCode ||
        lastError?.response?.headers?.get?.('x-ms-error-code');
      const message = lastError?.message || 'Upload failed';
      throw new Error(
        `${message}${status ? ` (status ${status})` : ''}${
          code ? ` [${code}]` : ''
        }`
      );
    },

    // HEAD existence check using read SAS
    checkFileExists: async (fileName: string): Promise<boolean> => {
      try {
        const blobPath = buildBlobPath(fileName);
        const sasUrl = await requestSasUrl(blobPath, 'r', 15);
        const resp = await fetch(sasUrl, {method: 'HEAD'});
        if (resp.status === 200) return true;
        return false; // 404 or other statuses treated as not existing
      } catch (_e) {
        // Network/CORS issues or 404s should not block uploading; assume not exists
        console.warn('checkFileExists failed, assuming file does not exist:', (_e as any)?.message || _e);
        return false;
      }
    },

    // Not implemented in frontend; provide a backend route if needed
    deleteFile: async (_filePath: string): Promise<void> => {
      // deleteFile not implemented in SAS flow. Provide backend delete endpoint if required.
    },
  };

  return service;
};

// Informational URL builder for display only
export const generateAzureStorageUrl = (
  fileName: string,
  _moderatorId?: string,
  _homeId?: string
): string => {
  const path = buildBlobPath(fileName);
  return `https://${AZURE_CONFIG.accountName}.blob.core.windows.net/${AZURE_CONFIG.containerName}/${path}`;
};

export const validateAzureStorageConfig = (): boolean => {
  return !!(
    AZURE_CONFIG.accountName &&
    AZURE_CONFIG.containerName &&
    AZURE_CONFIG.directoryName
  );
};

export const getAzureStorageConfig = (): AzureStorageConfig => {
  return {...AZURE_CONFIG};
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const validateFileType = (
  file: File,
  allowedTypes: string[]
): boolean => {
  return allowedTypes.includes(file.type);
};

export const generateFileMetadata = (
  file: File,
  moderatorId: string,
  homeId: string
): Record<string, string> => {
  return {
    'Content-Type': file.type,
    'Content-Length': file.size.toString(),
    'Moderator-ID': moderatorId,
    'Home-ID': homeId,
    'Upload-Date': new Date().toISOString(),
    'Original-Name': file.name,
  };
};

// List blobs via backend endpoint (uses account key on server)
export const listAzureFiles = async (params?: {
  prefix?: string;
  homeId?: string;
  maxResults?: number;
  marker?: string | null;
}): Promise<{
  blobs: AzureBlobItem[];
  container: string;
  prefix: string;
  count: number;
  nextMarker: string | null;
}> => {
  const base = apiConfig.baseURL.replace(/\/$/, '');
  const url = new URL(`${base}/uploads/list`);
  if (params?.prefix) url.searchParams.set('prefix', params.prefix);
  if (params?.homeId) url.searchParams.set('homeId', params.homeId);
  if (typeof params?.maxResults === 'number')
    url.searchParams.set('maxResults', String(params.maxResults));
  if (params?.marker) url.searchParams.set('marker', params.marker);

  const resp = await fetch(url.toString(), {headers: authHeaders()});
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to list blobs: ${resp.status} ${text}`);
  }
  return resp.json();
};

/**
 * Detect file format based on MIME type and extension
 */
export const getFileFormat = (file: File): string => {
  const mimeType = file.type.toLowerCase();
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  
  // Video formats
  if (mimeType.startsWith('video/') || 
      ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'm4v'].includes(extension)) {
    return 'video';
  }
  
  // Audio formats
  if (mimeType.startsWith('audio/') || 
      ['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg', 'wma'].includes(extension)) {
    return 'audio';
  }
  
  // Image formats
  if (mimeType.startsWith('image/') || 
      ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff'].includes(extension)) {
    return 'image';
  }
  
  // Document formats
  if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'].includes(extension)) {
    return 'document';
  }
  
  // Special formats
  if (extension === 'insv' || extension === 'lrv') {
    return 'insv';
  }
  
  // Archive formats
  if (['zip', 'tar', 'gz', 'rar', '7z', 'tgz'].includes(extension)) {
    return 'archive';
  }
  
  // Default
  return 'other';
};

/**
 * Build upload path: <directory>/<projectFolder>/<uploadDate>/<extension>/<fileName>
 * All files (regular and bulk uploads) use the same structure.
 * The project's folder must already be loaded (loadProjectStorageFolder).
 */
export const buildFileUploadPath = (
  projectId: string,
  file: File,
  options?: {
    uploadDate?: string; // YYYY-MM-DD format, defaults to today
    format?: string;      // Overrides the extension folder (e.g. 'json')
    isBulkUpload?: boolean; // Kept for backward compatibility but not used in path
  }
): string => {
  const uploadDate = options?.uploadDate || getLocalDateString();
  return buildProjectFilePath(
    projectId,
    uploadDate,
    file.name,
    AZURE_CONFIG.directoryName,
    options?.format
  );
};

/**
 * Build JSON metadata path from file path
 * Removes the original file extension and replaces it with .json
 * Example: video.mp4 -> video.json (not video.mp4.json)
 * If the path already ends with .json, return as is (for skip entries)
 */
export const buildJsonMetadataPath = (filePath: string): string => {
  // If path already ends with .json, return as is (for skip entries)
  if (filePath.toLowerCase().endsWith('.json')) {
    return filePath;
  }
  
  // Remove the original file extension and add .json
  // Find the last dot before the last slash (to handle paths like dir/file.ext)
  const lastSlashIndex = filePath.lastIndexOf('/');
  const fileName = lastSlashIndex >= 0 ? filePath.substring(lastSlashIndex + 1) : filePath;
  const lastDotIndex = fileName.lastIndexOf('.');
  
  if (lastDotIndex > 0) {
    // File has an extension, replace it with .json
    const baseName = fileName.substring(0, lastDotIndex);
    const directory = lastSlashIndex >= 0 ? filePath.substring(0, lastSlashIndex + 1) : '';
    return `${directory}${baseName}.json`;
  }
  
  // No extension found, just add .json
  return `${filePath}.json`;
};

/**
 * Upload JSON metadata file to Azure
 */
export const uploadMetadataJson = async (
  jsonData: Record<string, any>,
  filePath: string,
  moderatorId: string,
  homeId: string
): Promise<string> => {
  try {
    const jsonPath = buildJsonMetadataPath(filePath);
    const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], {
      type: 'application/json',
    });
    const jsonFile = new File([jsonBlob], jsonPath.split('/').pop() || 'metadata.json', {
      type: 'application/json',
    });

    const azureService = await createAzureStorageService(moderatorId, homeId);
    // Pass path as-is - if it includes '/', uploadFile will use it as-is
    // If it doesn't include '/', uploadFile will add directory prefix
    // Since jsonPath may or may not have directory prefix, we pass it as-is
    const uploadedPath = await azureService.uploadFile(
      jsonFile,
      jsonPath, // Pass path as-is - uploadFile handles both cases
      moderatorId,
      homeId,
      { overwrite: true }
    );
    
    return uploadedPath;
  } catch (error: any) {
    console.error('Error uploading JSON metadata:', error);
    throw new Error(`Failed to upload JSON metadata: ${error.message}`);
  }
};

/**
 * Download and parse JSON metadata from Azure
 */
export const downloadMetadataJson = async (
  filePath: string
): Promise<Record<string, any> | null> => {
  try {
    const jsonPath = buildJsonMetadataPath(filePath);
    const blobPath = isRootedBlobPath(jsonPath) ? jsonPath : buildBlobPath(jsonPath);
    const sasUrl = await requestSasUrl(blobPath, 'r', 15);
    
    const response = await fetch(sasUrl);
    if (!response.ok) {
      if (response.status === 404) {
        return null; // JSON doesn't exist yet
      }
      throw new Error(`Failed to download JSON: ${response.status}`);
    }
    
    const jsonData = await response.json();
    return jsonData;
  } catch (error: any) {
    if (error.message.includes('404') || error.message.includes('not found')) {
      return null; // JSON doesn't exist
    }
    console.error('Error downloading JSON metadata:', error);
    throw error;
  }
};

/**
 * Update existing JSON metadata by merging new data
 */
export const updateMetadataJson = async (
  filePath: string,
  updates: Record<string, any>,
  moderatorId: string,
  homeId: string
): Promise<string> => {
  try {
    // Try to fetch existing JSON
    const existingJson = await downloadMetadataJson(filePath);
    
    // Merge updates into existing JSON (or create new if doesn't exist)
    const mergedJson = existingJson 
      ? { ...existingJson, ...updates, updatedAt: new Date().toISOString() }
      : { ...updates, createdAt: new Date().toISOString() };
    
    // Upload merged JSON
    return await uploadMetadataJson(mergedJson, filePath, moderatorId, homeId);
  } catch (error: any) {
    console.error('Error updating JSON metadata:', error);
    throw new Error(`Failed to update JSON metadata: ${error.message}`);
  }
};

/**
 * Calculate the size of a JSON object when stringified
 */
export const calculateJsonFileSize = (jsonData: Record<string, any>): number => {
  const jsonString = JSON.stringify(jsonData, null, 2);
  return new Blob([jsonString]).size;
};

/**
 * Build file path for skip entry (JSON metadata only)
 */
export const buildSkipEntryFilePath = (
  projectId: string,
  sessionTitle: string,
  uploadDate?: string
): string => {
  const date = uploadDate || getLocalDateString();
  const sanitizedTitle = sessionTitle
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '');
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const fileName = `${sanitizedTitle}-${timestamp}.json`;
  
  // Create a dummy file object for path building (format will be 'json' for JSON files)
  const dummyFile = new File([''], fileName, { type: 'application/json' });
  return buildFileUploadPath(projectId, dummyFile, {
    uploadDate: date,
    format: 'json', // Use 'json' extension instead of 'document'
    isBulkUpload: false,
  });
};

/**
 * Prepare session details JSON data with metadata
 */
export const prepareSessionDetailsJsonData = (
  sessionDetails: Record<string, any>,
  projectId: string,
  taskId: string,
  taskName: string,
  submittedBy: string
): Record<string, any> => {
  return {
    ...sessionDetails,
    submitted_at: new Date().toISOString(),
    submitted_by: submittedBy,
    project_id: projectId,
    task_id: taskId,
    task_name: taskName,
  };
};
